import { DOCUMENT, Injectable, inject } from '@angular/core';
import { EMPTY, Observable, defer, filter, fromEvent, ignoreElements, interval, map, merge, tap, throttleTime } from 'rxjs';
import { CLOCK } from '@app/core/time/clock';
import { SessionEndReason } from './session-end-reason';
import { SESSION_TIMING } from './session-timing';

/**
 * Lo que cuenta como actividad del cliente, igual en desktop y mobile. Pointer Events cubre mouse, touch y lápiz;
 * `input` suma el autocompletado y el dictado, que no emiten `keydown`.
 */
export const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'input', 'wheel', 'scroll'] as const;

// capture: `scroll` no burbujea, y un componente puede cortar la propagación. passive: nunca frena el scroll.
const LISTENER_OPTIONS = { passive: true, capture: true };

/**
 * Detecta la inactividad del cliente. Solo escucha eventos del DOM: ni las respuestas HTTP ni el polling
 * cuentan como actividad (si contaran, la consulta de cada minuto haría que la sesión no venza nunca).
 */
@Injectable({ providedIn: 'root' })
export class ActivityTracker {
  private readonly document = inject(DOCUMENT);
  private readonly clock = inject(CLOCK);

  /** Volver a la pestaña o restaurarla desde el caché del botón Atrás. NO es actividad: solo pide una revisión ya. */
  readonly backToForeground$: Observable<unknown> = merge(
    fromEvent(this.document, 'visibilitychange'),
    this.document.defaultView ? fromEvent(this.document.defaultView, 'pageshow') : EMPTY,
  ).pipe(filter(() => this.document.visibilityState === 'visible'));

  /**
   * Emite `inactivity` cuando pasan `inactivityLimitMs` sin interacción, contando desde que se suscribe.
   * Revisa cada `idleCheckMs` y al volver a la pestaña. Compara la hora de la última interacción contra el reloj
   * en vez de armar un timer de 60 min: el navegador mobile congela los timers en segundo plano, la hora no.
   * Mientras está suscripto escucha la actividad; al desuscribirse saca sus listeners.
   */
  watch(): Observable<SessionEndReason> {
    return defer(() => {
      let lastActivityAt = this.now();
      const activity$ = merge(...ACTIVITY_EVENTS.map((name) => fromEvent(this.document, name, LISTENER_OPTIONS))).pipe(
        throttleTime(SESSION_TIMING.activityThrottleMs),
        tap(() => (lastActivityAt = this.now())),
        ignoreElements(),
      );
      const idle$ = merge(interval(SESSION_TIMING.idleCheckMs), this.backToForeground$).pipe(
        filter(() => this.now() - lastActivityAt >= SESSION_TIMING.inactivityLimitMs),
        map((): SessionEndReason => 'inactivity'),
      );
      return merge(activity$, idle$);
    });
  }

  private now(): number {
    return this.clock().getTime();
  }
}
